import Head from "next/head";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { Seomatic } from "react-seomatic";

import ColumnLayout from "@components/ColumnLayout";
import EntryList from "@components/EntryList";
import FilterGroup from "@components/FilterGroup";
import Message from "@components/Message";
import PageTitle from "@components/PageTitle";
import Pagination from "@components/Pagination";
import SearchInput from "@components/SearchInput";
import SidebarCallToAction from "@components/SidebarCallToAction";
import useSetHeaderTheme from "@hooks/useSetHeaderTheme";
import Layout from "@layouts/Layout";
import sample from "lodash/sample";

const AttackLibraryLayout = (page) => {
	const {
		pageEntry,
		attacks,
		attackVectors,
		attackGoals,
		attackTactics,
		attackImpersonatedParties,
		attackImpersonatedBrands,
		attackThemes,
		attackTypes,
		attackLanguage,
		attackAiGenerated,
		navigation,
		category,
		searchQuery,
		pageCount,
		pageNumber,
		blogIndex,
	} = page;
	useSetHeaderTheme("dark");

	const router = useRouter();

	const [showVectors, setShowVectors] = useState(false);
	const [showGoals, setShowGoals] = useState(false);
	const [showTactics, setShowTactics] = useState(false);
	const [showParties, setShowParties] = useState(false);
	const [showTypes, setShowTypes] = useState(false);
	const [showThemes, setShowThemes] = useState(false);
	const [showBrands, setShowBrands] = useState(false);
	const [showLanguage, setShowLanguage] = useState(false);
	const [showAiGenerated, setShowAiGenerated] = useState(false);

	const toggleVisibility = function (status, updateFunction) {
		updateFunction(!status);
	};

	// Handle fetching status
	const [isFetching, setIsFetching] = useState(false);

	const handleSearch = (value) => {
		setIsFetching(true);

		router.push(`/attack-library/search?q=${value}`, undefined, {
			scroll: false,
		});
	};

	useEffect(() => {
		if (category) {
			setShowVectors(category.groupHandle === "attackVectors");
			setShowGoals(category.groupHandle === "attackGoals");
			setShowTactics(category.groupHandle === "attackTactics");
			setShowParties(category.groupHandle === "attackImpersonatedParties");
			setShowTypes(category.groupHandle === "attackTypes");
			setShowThemes(category.groupHandle === "attackThemes");
			setShowBrands(category.groupHandle === "attackImpersonatedBrands");
			setShowLanguage(category.groupHandle === "attackLanguage");
			setShowAiGenerated(category.groupHandle === "attackAiGenerated");
		}

		const handleRouteChange = () => {
			setIsFetching(false);
		};

		router.events.on("routeChangeComplete", handleRouteChange);

		return () => {
			router.events.off("routeChangeComplete", handleRouteChange);
		};
	}, [router]);
	const sidebarCallToAction = sample(blogIndex?.callToActions);

	return (
		<Layout navigation={navigation}>
			<Seomatic Head={Head} {...pageEntry.seomatic} />
			<PageTitle summary={pageEntry.summary}>{pageEntry.title}</PageTitle>
			<div className="AttackVault__container bg-white-01 py-15 px-5">
				<ColumnLayout disableMobileYSpace>
					<ColumnLayout.MainColumn>
						<EntryList
							entries={attacks}
							hideImage={true}
							showSummary={true}
							largeHeading={true}
							showReadMore={true}
							toggleLinks={true}
							hideCategory={true}
							hideDate={true}
							readMoreText={"See Attack Details"}
						/>
						<div className="AttackVault__pagination my-15">
							<Pagination
								pageCount={pageCount}
								activePage={pageNumber}
								basePath="/attack-library"
							/>
						</div>
						{attacks.length === 0 && !!searchQuery && (
							<Message message="No attacks match your search criteria. Please try again." />
						)}
					</ColumnLayout.MainColumn>
					<ColumnLayout.SidebarColumn>
						<div className="AttackVault__filters bg-yellow-01 px-10 py-15 mb-7.5">
							<h2 className="text-2xl mb-5 font-medium">Filters</h2>
							<SearchInput
								onCommit={handleSearch}
								isFetching={isFetching}
								initialValue={searchQuery}
								noMargin
								className={"mb-10 border-0"}
							/>

							<FilterGroup
								onClick={() => toggleVisibility(showTypes, setShowTypes)}
								categories={attackTypes}
								activeCategory={category}
								showContent={showTypes}
								headingText="Attack Type"
								baseUrl="/attack-library"
							/>

							<FilterGroup
								onClick={() => toggleVisibility(showParties, setShowParties)}
								categories={attackImpersonatedParties}
								activeCategory={category}
								showContent={showParties}
								headingText="Impersonated Party"
								baseUrl="/attack-library"
							/>

							<FilterGroup
								onClick={() => toggleVisibility(showBrands, setShowBrands)}
								categories={attackImpersonatedBrands}
								activeCategory={category}
								showContent={showBrands}
								headingText="Impersonated Brand"
								baseUrl="/attack-library"
							/>

							<FilterGroup
								onClick={() => toggleVisibility(showGoals, setShowGoals)}
								categories={attackGoals}
								activeCategory={category}
								showContent={showGoals}
								headingText="Attack Goal"
								baseUrl="/attack-library"
							/>

							<FilterGroup
								onClick={() => toggleVisibility(showVectors, setShowVectors)}
								categories={attackVectors}
								activeCategory={category}
								showContent={showVectors}
								headingText="Attack Vector"
								baseUrl="/attack-library"
							/>

							<FilterGroup
								onClick={() => toggleVisibility(showTactics, setShowTactics)}
								categories={attackTactics}
								activeCategory={category}
								showContent={showTactics}
								headingText="Attack Tactic"
								baseUrl="/attack-library"
							/>

							<FilterGroup
								onClick={() => toggleVisibility(showThemes, setShowThemes)}
								categories={attackThemes}
								activeCategory={category}
								showContent={showThemes}
								headingText="Attack Theme"
								baseUrl="/attack-library"
							/>

							<FilterGroup
								onClick={() => toggleVisibility(showLanguage, setShowLanguage)}
								categories={attackLanguage}
								activeCategory={category}
								showContent={showLanguage}
								headingText="Attack Language"
								baseUrl="/attack-library"
							/>

							<FilterGroup
								onClick={() =>
									toggleVisibility(showAiGenerated, setShowAiGenerated)
								}
								categories={attackAiGenerated}
								activeCategory={category}
								showContent={showAiGenerated}
								headingText="AI-Generated"
								baseUrl="/attack-library"
							/>

							<div className="AttackVault__filters__button-container text-right mt-7.5">
								<a
									className="inline-block border-b-2 font-medium"
									href="/attack-library"
								>
									Reset Filters
								</a>
							</div>
						</div>
						{!!sidebarCallToAction && (
							<SidebarCallToAction
								heading={sidebarCallToAction.heading}
								image={sidebarCallToAction.image[0]}
								imageBlur={sidebarCallToAction.imageBlur[0]}
								target={sidebarCallToAction.target}
							/>
						)}
					</ColumnLayout.SidebarColumn>
				</ColumnLayout>
			</div>
		</Layout>
	);
};

export default AttackLibraryLayout;
